/**
 * The ONE presentation source for every maintained composition profile: the
 * coded render manifest the compiler emits AND the generated media that
 * replaces it. Every media slot is DERIVED from this declaration — never a
 * second hand-written list — so a new profile (or a composed spec) that
 * declares its render entities here gets sprites, animation strips, a
 * background, a cover and a soundtrack with no further media code.
 *
 * Contract for profile authors (bump-arcade@2, flight-strike@1, composed
 * specs, …):
 *  - every `RenderEntity.kind` the adapter emits is a key of `entities`
 *    (kebab-case slug; never a reserved slot name, never ending in
 *    `-<animation state>` so a kind can never read as a strip slot);
 *  - `states` lists EXACTLY the `RenderEntity.state` values the adapter emits
 *    for that kind (closed {@link ANIM_STATE_NAMES} vocabulary, `[]` for a
 *    kind that never sets a state). Each declared state becomes one generated
 *    animation strip `<kind>-<state>` bound into `entities[kind].frames`; a
 *    state the adapter emits but does not declare plays the static sprite, a
 *    declared state it never emits is paid for and never seen;
 *  - `role` is a short noun phrase of what the kind IS — the subject its one
 *    sprite draws, never its situation in play (`subjectOnly.ts`);
 *    it feeds the art recommender and the default art direction;
 *  - `art: 'neutral'` only for a kind the adapter recolours per instance
 *    (`RenderEntity.tint`); its art is generated grayscale and bound with
 *    `recolor`;
 *  - `derivedFrom: <kind>` for a kind whose art is another kind's accepted
 *    art recoloured to its own palette colour (a composed versus rival): its
 *    slots exist and are bound like any other, but are never generated or
 *    judged, and are deferred exactly when their source slots are
 *    ({@link compositionDerivedSlotsV1});
 *  - at most {@link HERO_SHEET_MAX_SPRITES} kinds (one legible hero sheet);
 *  - `music` is the closed, ADR-0009-bounded soundtrack direction (musical
 *    fields only — never the title, design or concept).
 */
import { ANIM_STATE_NAMES, isAnimStateName, stripAssetName, type AnimStateName } from '../animation.js';
import { validateMusicBriefV1, type MusicBriefV1 } from '../audioGeneration.js';
import { HERO_SHEET_MAX_SPRITES } from '../design/artPlan.js';
import { validateRenderManifest, type EntityRender, type RenderManifest } from '../renderManifest.js';
import { isSpriteLabel } from '../design/types.js';
import type { CompositionGameSpecV1 } from './gameSpec.js';
import { getTemplateIn, seatRelation, type GameSpecV1 } from '@djgg/gameplay-kit/compose';
import type { PlayModeId } from './registry.js';
import { COMPOSED_PROFILE, composedGameV1, composedSpecLibraryV1 } from './composedSpec.js';
import { composedCompositionPresentationV1, composedRenderManifestV1 } from './composedPresentation.js';

export const COMPOSITION_BACKGROUND_SLOT = 'background';
/** The generated gallery cover (16:9 key art). Attached to the creation, never
 * bundled into the game. */
export const COMPOSITION_COVER_SLOT = 'cover';
/** The generated soundtrack, bound as the bundle's looping match music. */
export const COMPOSITION_MUSIC_SLOT = 'music';
const RESERVED_SLOTS: readonly string[] = [COMPOSITION_BACKGROUND_SLOT, COMPOSITION_COVER_SLOT, COMPOSITION_MUSIC_SLOT];
/** Per-build cap on declared animation strips. The old pipeline's STRIP_CAP
 * was 15; composed templates derive every kind's states from its modules
 * (sewer-1983 declares 17, stolen-wings 18, bounty-bob 12), so the cap is
 * 24 — the paid allowance still derives from the exact declared count. */
export const COMPOSITION_MAX_STRIP_SLOTS = 24;

export type CompositionProjectionV1 = 'side' | 'top-down' | 'front';

/** How a kind's art faces within the game's projection:
 *  - `side`: it has a left/right facing and is drawn facing right (the shell
 *    mirrors it for left) — a walking or flying actor;
 *  - `front`: it faces the viewer, turned neither left nor right — a block,
 *    door, pickup or prop;
 *  - `symmetric`: it has no facing at all and is drawn left-right symmetric;
 *  - `diagonal`: a three-quarter view whose nose points up-right along a
 *    diagonal scroll, never mirrored — every craft of a pseudo-isometric
 *    flight game. */
export type CompositionFacingV1 = 'side' | 'front' | 'symmetric' | 'diagonal';
export const COMPOSITION_FACINGS: readonly CompositionFacingV1[] = ['side', 'front', 'symmetric', 'diagonal'];

export interface CompositionPresentationEntityV1 {
  role: string;
  /** Present only for a kind the adapter recolours per instance. */
  art?: 'neutral';
  /** Exactly the animation states the adapter emits for this kind. */
  states: AnimStateName[];
  render: EntityRender;
  /** Declared facing; absent ⇒ {@link compositionFacingV1}'s role default. */
  facing?: CompositionFacingV1;
  /** The one short sign the kind's art carries, verbatim ("POW"). There is no
   * runtime text-on-entity layer, so a kind whose identity IS its sign
   * declares it here and the generator prints it (the judge allows exactly
   * it). Uppercase letters, digits, spaces and !?- only. */
  label?: string;
  /** The (non-derived) kind whose accepted art this kind's is recoloured
   * from, to this kind's palette colour — zero generation calls. */
  derivedFrom?: string;
}

/** A kind's facing: its declaration, else the role default — a kind the
 * adapter animates (declared states) is an actor that faces its motion; a
 * stateless kind is a prop/pickup/surface that faces the viewer. */
export function compositionFacingV1(entity: CompositionPresentationEntityV1): CompositionFacingV1 {
  return entity.facing ?? (entity.states.length > 0 ? 'side' : 'front');
}

export interface CompositionPresentationV1 {
  /** Genre phrase for generated art ("a single-screen platform arcade game"). */
  summary: string;
  projection: CompositionProjectionV1;
  backgroundRole: string;
  world: RenderManifest['world'];
  camera?: RenderManifest['camera'];
  palette: string[];
  backgroundPaletteIndex: number;
  entities: Record<string, CompositionPresentationEntityV1>;
  /** True when a body in this game stands on, walks along, climbs or lands on
   * a surface the game draws itself (a platformer, a lander): its background
   * must paint no playable structure. A composed game derives it from its
   * modules; a maintained profile declares it. `sceneryOnly.ts` reads it. */
  surfaceContact: boolean;
  /** Soundtrack direction for the generated music slot. */
  music: MusicBriefV1;
}

type CompositionSpec = CompositionGameSpecV1;

const ARCADE = ['#dfe7ff', '#5ad2ff', '#ffd24a', '#0a1020', '#ff4466'];
const NIGHT = ['#aaa4c7', '#b4ffdc', '#ffed9c', '#151129', '#ff6781'];
const rectangle = (w: number, h: number, palette: number, depth: number, extra: Partial<EntityRender> = {}): EntityRender =>
  ({ sprite: 'builtin:rectangle', palette, size: { w, h }, depth, ...extra });
const circle = (w: number, h: number, palette: number, depth: number, extra: Partial<EntityRender> = {}): EntityRender =>
  ({ ...rectangle(w, h, palette, depth, extra), sprite: 'builtin:circle' });
const music = (mood: string, genre: string, tempoBpm: number, instrumentation: string[]): MusicBriefV1 =>
  ({ mood, genre, tempoBpm, instrumentation });

function presentation(spec: CompositionSpec): CompositionPresentationV1 {
  switch (spec.profile) {
    case COMPOSED_PROFILE: {
      // Derived from the game's modules: each kind's states are exactly the
      // states its modules can put on it (what the composed adapter emits);
      // the soundtrack direction is the template's.
      const library = composedSpecLibraryV1(spec);
      const p = composedCompositionPresentationV1(composedGameV1(spec), library);
      const t = getTemplateIn(library, spec.template).music;
      return {
        summary: p.summary, projection: p.projection, backgroundRole: p.backgroundRole, world: p.world,
        ...(p.camera ? { camera: p.camera } : {}),
        palette: p.palette, backgroundPaletteIndex: p.backgroundPaletteIndex, surfaceContact: p.surfaceContact,
        music: music(t.mood, t.genre, t.tempoBpm, [...t.instrumentation]),
        entities: Object.fromEntries(Object.entries(p.entities).map(([kind, { role, art, render, states, facing, label, derivedFrom }]) =>
          [kind, { role, ...(art ? { art } : {}), states: [...states], render, ...(facing ? { facing } : {}), ...(label !== undefined ? { label } : {}),
            ...(derivedFrom !== undefined ? { derivedFrom } : {}) }])),
      };
    }
    case 'courier-short@1':
    case 'courier-boost@1':
      return {
        summary: 'a side-scrolling courier platform run',
        surfaceContact: true,
        music: music('upbeat determined delivery run', 'chiptune platformer', 132, ['square lead', 'triangle bass', 'noise drums']),
        projection: 'side',
        backgroundRole: 'the side-view scenery behind the route',
        world: { w: 16, h: 9 },
        camera: { level: { w: 24, h: 9 }, lookAhead: 3, ease: 0.14 },
        palette: spec.theme === 'courier' ? ARCADE : NIGHT,
        backgroundPaletteIndex: 3,
        entities: {
          runner: { role: 'the player courier', states: ['idle', 'move', 'jump', 'hit'], render: rectangle(0.9, 1.4, 1, 10, { anchor: 'bottom', shadow: true }) },
          platform: { role: 'a solid ground platform block', states: [], render: rectangle(4, 1.5, 0, 0, { surface: true }) },
          spike: { role: 'a spike hazard', states: [], render: circle(0.8, 0.6, 4, 4, { anchor: 'bottom' }) },
          parcel: { role: 'a collectible parcel', states: [], render: rectangle(0.6, 0.6, 2, 5, { anchor: 'bottom', ambient: 'pulse', leave: 'shrink' }) },
          exit: { role: 'the delivery exit door', states: [], render: rectangle(1, 1.6, 2, 3, { anchor: 'bottom' }) },
        },
      };
    case 'bump-arcade@1':
      return {
        summary: 'a single-screen platform arcade game',
        surfaceContact: true,
        music: music('bouncy playful arcade action', 'retro arcade chiptune', 140, ['square lead', 'pulse arpeggio', 'triangle bass', 'noise drums']),
        projection: 'side',
        backgroundRole: 'the single-screen arena backdrop',
        world: { w: 20, h: 11 },
        palette: spec.theme === 'arcade' ? ARCADE : NIGHT,
        backgroundPaletteIndex: 3,
        entities: {
          player: { role: 'the player hero', states: ['idle', 'move', 'jump', 'hit'], render: rectangle(0.9, 1.4, 1, 10, { anchor: 'bottom', shadow: true }) },
          enemy: { role: 'a walking wave enemy', states: ['move', 'down'], render: rectangle(0.9, 0.8, 4, 5, { anchor: 'bottom' }) },
          platform: { role: 'a solid floor platform bumped from below', states: [], render: rectangle(4, 1, 0, 0, { surface: true }) },
          pow: { role: 'the POW block', label: 'POW', states: [], render: rectangle(1.6, 0.6, 2, 2, { surface: true, surfaceArt: 'once' }) },
          pipe: { role: 'an enemy spawn pipe', states: [], render: rectangle(1.1, 1.25, 1, 3, { anchor: 'bottom' }) },
        },
      };
    case 'territory@1':
      return {
        summary: 'a top-down territory capture game',
        surfaceContact: false,
        music: music('tense focused pursuit', 'minimal synth arcade', 120, ['analog synth lead', 'sub bass', 'electronic drums']),
        projection: 'top-down',
        backgroundRole: 'the board backdrop',
        // The 12×10 simulation keeps four-unit presentation gutters on each
        // side, separating the board from native controls and the HUD.
        world: { w: 20, h: 10 },
        palette: spec.theme === 'arcade' ? ARCADE : NIGHT,
        backgroundPaletteIndex: 3,
        entities: {
          actor: { role: 'the player avatar', states: ['idle', 'move', 'hit'], render: rectangle(0.68, 0.68, 1, 10) },
          territory: { role: 'a claimed safe territory tile', states: [], render: rectangle(0.94, 0.94, 0, 0) },
          trail: { role: 'an exposed capture-trail tile', states: [], render: rectangle(0.74, 0.74, 2, 2) },
          hazard: { role: 'a roaming hazard', states: ['move'], render: circle(0.68, 0.68, 4, 5) },
        },
      };
    case 'shooter-mission@1':
      return {
        summary: 'a horizontally scrolling shooter mission',
        surfaceContact: false,
        music: music('driving heroic air battle', 'arcade shooter rock', 150, ['overdriven synth lead', 'electric bass', 'rock drums']),
        projection: 'side',
        backgroundRole: 'the scrolling sky and terrain backdrop',
        world: { w: 16, h: 12 },
        camera: { level: { w: 44, h: 12 }, lookAhead: 3, ease: 0.14 },
        palette: spec.theme === 'arcade' ? ARCADE : NIGHT,
        backgroundPaletteIndex: 3,
        entities: {
          pilot: { role: 'the player aircraft', states: ['idle', 'move', 'hit'], render: rectangle(1, 0.6, 1, 10) },
          enemy: { role: 'an enemy aircraft', states: ['idle', 'hit'], render: rectangle(1, 0.8, 4, 5) },
          cannon: { role: 'the player cannon shot', states: [], render: rectangle(0.35, 0.16, 2, 6) },
          bomb: { role: 'the player bomb', states: [], render: circle(0.3, 0.3, 2, 6) },
          'enemy-shot': { role: 'an enemy projectile', states: [], render: rectangle(0.25, 0.25, 4, 6) },
        },
      };
    case 'match-puzzle@1':
      return {
        summary: 'a grid match puzzle',
        surfaceContact: false,
        music: music('light bright and thoughtful', 'casual puzzle pop', 104, ['marimba', 'soft synth pad', 'light percussion']),
        projection: 'front',
        backgroundRole: 'the puzzle board backdrop',
        // The 5×5 simulation is centered between two-unit gutters with a
        // one-unit HUD row, preserving its accepted landscape presentation.
        world: { w: 9, h: 6 },
        // Four admitted gem kinds use entries 1–4; background has its own
        // entry so a valid piece never becomes invisible against the board.
        palette: spec.theme === 'arcade'
          ? ['#dfe7ff', '#5ad2ff', '#ffd24a', '#b58aff', '#ff4466', '#0a1020']
          : ['#eee9ff', '#b4ffdc', '#ffed9c', '#c1a4ff', '#ff6781', '#151129'],
        backgroundPaletteIndex: 5,
        entities: {
          gem: { role: 'a matchable gem, recoloured per gem type', art: 'neutral', states: ['idle', 'move'], render: rectangle(0.76, 0.76, 1, 10) },
        },
      };
    case 'service-game@1':
      return {
        summary: 'a kitchen service shift game',
        surfaceContact: false,
        music: music('busy cheerful kitchen rush', 'swing jazz', 128, ['upright bass', 'brushed drums', 'piano', 'muted trumpet']),
        projection: 'front',
        backgroundRole: 'the kitchen backdrop',
        world: { w: 16, h: 10 },
        palette: spec.theme === 'arcade' ? ARCADE : NIGHT,
        backgroundPaletteIndex: 3,
        entities: {
          'service-stock': { role: 'an ingredient stock bin', states: ['idle', 'action-1', 'down'], render: rectangle(2.2, 2, 1, 10) },
          'service-prep': { role: 'a preparation station', states: ['idle', 'action-1', 'down'], render: rectangle(2.5, 2, 2, 10) },
          'service-cook': { role: 'a cooking station', states: ['idle', 'action-1', 'down'], render: rectangle(2.5, 2, 4, 10) },
          'service-serve': { role: 'a serving counter', states: ['idle', 'action-1', 'down'], render: rectangle(2.5, 4, 1, 10) },
          'service-ticket': { role: 'an order ticket', states: ['idle', 'win'], render: rectangle(2.8, 1.2, 0, 10) },
          'service-carry': { role: 'a carried ingredient', states: ['idle'], render: rectangle(0.8, 0.8, 2, 10) },
        },
      };
  }
}

/** A kind count art cannot present, or '' when it can. A data patch cannot
 * change the count, so a stage that adds kinds checks this while it can repair. */
export function compositionEntityKindCountErrorV1(count: number): string {
  return count < 1 || count > HERO_SHEET_MAX_SPRITES ? `the game has ${count} entity kinds, and art supports 1..${HERO_SHEET_MAX_SPRITES}` : '';
}

/** Exact presentation for an accepted spec (a fresh copy; callers may mutate). */
export function compositionPresentationV1(spec: CompositionSpec): CompositionPresentationV1 {
  const value = structuredClone(presentation(spec));
  const kinds = Object.keys(value.entities);
  const count = compositionEntityKindCountErrorV1(kinds.length);
  if (count) throw new Error(`Invalid composition presentation entity kinds: ${count}`);
  if (kinds.some(kind => !/^[a-z][a-z0-9-]{0,39}$/.test(kind) || RESERVED_SLOTS.includes(kind)
      || ANIM_STATE_NAMES.some(state => kind.endsWith(`-${state}`)))) {
    throw new Error('Invalid composition presentation entity kinds');
  }
  let strips = 0;
  for (const [kind, entity] of Object.entries(value.entities)) {
    if (!Array.isArray(entity.states) || new Set(entity.states).size !== entity.states.length
      || entity.states.some(state => !isAnimStateName(state))) {
      throw new Error(`Invalid composition presentation animation states for ${kind}`);
    }
    for (const state of entity.states) stripAssetName(kind, state);
    if ((entity.facing !== undefined && !COMPOSITION_FACINGS.includes(entity.facing))
      || (entity.label !== undefined && !isSpriteLabel(entity.label))) {
      throw new Error(`Invalid composition presentation facing or label for ${kind}`);
    }
    const source = entity.derivedFrom === undefined ? undefined : value.entities[entity.derivedFrom];
    if (entity.derivedFrom !== undefined && (!source || source === entity || source.derivedFrom !== undefined
      || entity.states.some(state => !source.states.includes(state)))) {
      throw new Error(`Invalid composition presentation derivation for ${kind}`);
    }
    strips += entity.states.length;
  }
  if (strips > COMPOSITION_MAX_STRIP_SLOTS) throw new Error('Composition presentation declares too many animation strips');
  value.music = validateMusicBriefV1(value.music);
  return value;
}

/** The coded (structural) render manifest the compiler emits. */
export function compositionRenderManifestV1(spec: CompositionSpec): RenderManifest {
  if (spec.profile === COMPOSED_PROFILE) return composedRenderManifestV1(composedGameV1(spec), composedSpecLibraryV1(spec));
  const p = compositionPresentationV1(spec);
  return validateRenderManifest({
    version: 7,
    world: p.world,
    ...(p.camera ? { camera: p.camera } : {}),
    palette: p.palette,
    entities: Object.fromEntries(Object.entries(p.entities).map(([kind, entity]) => [kind, entity.render])),
    background: { paletteIndex: p.backgroundPaletteIndex },
  });
}

/** The fixed play settings an accepted composition builds with: its play
 * modes (from its seat relation) and the orientation of its world (a 9x16
 * swarm is portrait, a 24x9 run is landscape) — derived, so neither a
 * portrait nor a two-player composed template needs a special case anywhere
 * in the route. */
export interface CompositionSettingsContractV1 { playModes: PlayModeId[]; orientation: 'landscape' | 'portrait' }

/** A composed game's play modes, from its seat relation: a versus match is
 * played against a bot, on one screen or online; every other game solo. */
export function composedPlayModesV1(game: GameSpecV1): PlayModeId[] {
  return seatRelation(game) === 'versus' ? ['vsBot', 'localVersus', 'onlineVersus'] : ['solo'];
}

export function compositionSettingsContractV1(spec: CompositionSpec): CompositionSettingsContractV1 {
  const { w, h } = compositionPresentationV1(spec).world;
  return { playModes: spec.profile === COMPOSED_PROFILE ? composedPlayModesV1(composedGameV1(spec)) : ['solo'],
    orientation: w >= h ? 'landscape' : 'portrait' };
}

/** Art-directed slots: every render entity kind (its base sprite) plus the
 * background, sorted. These are the slots a creator reviews a look for;
 * strips and the cover inherit their kind's/the game's look, and the music
 * has its own closed brief. */
export function compositionArtSlotsV1(spec: CompositionSpec): string[] {
  return [...Object.keys(compositionPresentationV1(spec).entities), COMPOSITION_BACKGROUND_SLOT].sort();
}

/** The render entities whose art is GENERATED (and judged): every kind but
 * a derived one, in declaration order. The ONE exclusion every generation
 * count and producer (call plan, statics design, strip batches) applies. */
export function compositionGeneratedEntitiesV1(presentation: Pick<CompositionPresentationV1, 'entities'>):
  Array<[string, CompositionPresentationEntityV1]> {
  return Object.entries(presentation.entities).filter(([, entity]) => entity.derivedFrom === undefined);
}

/** Every DERIVED slot of the spec → the source slot its art is recoloured
 * from: a derived kind's sprite from its source's sprite, and each of its
 * strips `<kind>-<state>` from the source's strip of that state. Sorted. */
export function compositionDerivedSlotsV1(spec: CompositionSpec): Map<string, string> {
  const entities = compositionPresentationV1(spec).entities;
  const pairs: Array<[string, string]> = [];
  for (const [kind, entity] of Object.entries(entities)) {
    if (entity.derivedFrom === undefined) continue;
    pairs.push([kind, entity.derivedFrom]);
    for (const state of entity.states) pairs.push([stripAssetName(kind, state), stripAssetName(entity.derivedFrom, state)]);
  }
  return new Map(pairs.sort(([a], [b]) => a.localeCompare(b)));
}

/** The palette colour a recolour SOURCE kind's art must wear — the colour
 * its derived kinds' recolour band is centred on — or undefined for a kind no
 * kind derives from. The ONE source of that colour: the source's image prompt
 * names it and {@link compositionRecolorV1} recolours from it, so the two can
 * never disagree. */
export function compositionRecolorSourceHexV1(presentation: Pick<CompositionPresentationV1, 'entities' | 'palette'>,
  kind: string): string | undefined {
  const entity = presentation.entities[kind];
  if (!entity || !Object.values(presentation.entities).some(other => other.derivedFrom === kind)) return undefined;
  return presentation.palette[entity.render.palette]!;
}

/** A DERIVED kind's recolour: its source kind and the palette colours it moves
 * from (the source's, {@link compositionRecolorSourceHexV1}) and to (its own). */
export function compositionRecolorV1(presentation: Pick<CompositionPresentationV1, 'entities' | 'palette'>, kind: string):
  { source: string; fromHex: string; toHex: string } {
  const source = presentation.entities[kind]?.derivedFrom;
  const fromHex = source === undefined ? undefined : compositionRecolorSourceHexV1(presentation, source);
  if (source === undefined || fromHex === undefined) throw new Error(`Composition kind ${kind} is not derived`);
  return { source, fromHex, toHex: presentation.palette[presentation.entities[kind]!.render.palette]! };
}

/** True when `missing` (slots of `slots` published without their own art)
 * is a legal gap: each is independently deferrable
 * ({@link compositionDeferrableSlotsV1}) or a derived slot whose source is
 * missing too, and every missing source takes its derived slots with it — a
 * derived slot is deferred exactly when its source is. */
export function compositionDeferralClosedV1(spec: CompositionSpec, slots: readonly string[], missing: readonly string[]): boolean {
  const derived = compositionDerivedSlotsV1(spec);
  const deferrable = compositionDeferrableSlotsV1(spec, slots);
  const gap = new Set(missing);
  return missing.every(slot => slots.includes(slot) && (deferrable.includes(slot) || gap.has(derived.get(slot) ?? '')))
    && [...derived].every(([slot, source]) => !slots.includes(slot) || gap.has(slot) === gap.has(source));
}

/** One animation strip slot: `<kind>-<state>` for one declared state. */
export interface CompositionAnimationSlotV1 {
  kind: string;
  state: AnimStateName;
  slot: string;
}

/** Every declared (kind, state) strip, sorted by slot. */
export function compositionAnimationSlotsV1(spec: CompositionSpec): CompositionAnimationSlotV1[] {
  return Object.entries(compositionPresentationV1(spec).entities)
    .flatMap(([kind, entity]) => entity.states.map(state => ({ kind, state, slot: stripAssetName(kind, state) })))
    .sort((a, b) => a.slot.localeCompare(b.slot));
}

/** Every generated-media slot, sorted: the art slots, one strip per declared
 * animation state, the cover, and — when the accepted authority generates a
 * soundtrack — the music. Derived; there is no per-profile slot list. */
export function compositionMediaSlotsV1(spec: CompositionSpec, options: { music: boolean }): string[] {
  return [
    ...compositionArtSlotsV1(spec),
    ...compositionAnimationSlotsV1(spec).map(strip => strip.slot),
    COMPOSITION_COVER_SLOT,
    ...(options.music ? [COMPOSITION_MUSIC_SLOT] : []),
  ].sort();
}

/** The slots bound into the game bundle: everything but the gallery cover,
 * which is attached to the creation instead. */
export function compositionBundleSlotsV1(slots: readonly string[]): string[] {
  return slots.filter(slot => slot !== COMPOSITION_COVER_SLOT);
}

/** THE decision of what a composed asset publish binds: an attached change with
 * the exact identity of a bundle slot. Everything else (a title overlay, art
 * named for a slot an earlier version had) is left alone and reported as not
 * published; the live game never reads it. */
export function partitionComposedPublishV1<T extends { name: string; kind: string; contentSha256?: string }>(
  changed: readonly T[],
  mediaSlots: readonly string[],
): { bind: T[]; notPublished: T[] } {
  const slots = compositionBundleSlotsV1(mediaSlots);
  const binds = (asset: T): boolean => slots.includes(asset.name)
    && asset.kind === compositionMediaSlotKindV1(asset.name) && !!asset.contentSha256;
  return { bind: changed.filter(binds), notPublished: changed.filter(asset => !binds(asset)) };
}

/** One art slot's role line, background included. */
export function compositionMediaSlotRolesV1(spec: CompositionSpec): Record<string, string> {
  const p = compositionPresentationV1(spec);
  return Object.fromEntries(compositionArtSlotsV1(spec).map(slot => [slot,
    slot === COMPOSITION_BACKGROUND_SLOT ? p.backgroundRole : p.entities[slot]!.role]));
}

export type CompositionMediaSlotKindV1 = 'background' | 'sprite' | 'strip' | 'cover' | 'music';

/** True when a kind's art is a TERRAIN TILE: a collision surface whose art
 * the shell repeats along its box (`surface: true`, tiled — a floor, girder,
 * catwalk, platform or ladder). A surface drawn ONCE (`surfaceArt: 'once'`, a
 * sign such as a POW block) is one object, not terrain. Read from the render
 * declaration, never from the kind's name. */
export function compositionTerrainTileV1(entity: Pick<CompositionPresentationEntityV1, 'render'>): boolean {
  return entity.render.surface === true && entity.render.surfaceArt !== 'once';
}

/** The slots of `slots` a composed version may publish DEFERRED (without
 * their own art) — exactly the ones with an approved stand-in a player can be
 * shown: an animation strip (its kind's still sprite), the background (a
 * themed palette backdrop, {@link compositionBackdropV1}) and any entity
 * sprite (the shell's compiled built-in visual). Generated artwork is optional
 * presentation and must not block a functional first playable. The cover and
 * soundtrack are not generic visual deferrals; the deferred-media validator has
 * one separate, reason-bound exception for a soundtrack whose provider call
 * ended without music (COMPOSITION_MUSIC_DEFERRAL_REASONS). A DERIVED
 * slot is never independently deferrable: it
 * is deferred exactly when its source is ({@link compositionDeferralClosedV1}). */
export function compositionDeferrableSlotsV1(spec: CompositionSpec, slots: readonly string[]): string[] {
  const entities = compositionPresentationV1(spec).entities;
  // A derived slot is never deferred on its own: it follows its source
  // (compositionDeferralClosedV1).
  const derived = compositionDerivedSlotsV1(spec);
  return slots.filter(slot => {
    const kind = compositionMediaSlotKindV1(slot);
    return !derived.has(slot) && (kind === 'strip' || kind === 'background'
      || (kind === 'sprite' && !!entities[slot]));
  });
}

/** The plain themed backdrop a version with a deferred background draws: a
 * vertical gradient from the presentation's background palette entry to its
 * darkest other entry (the shell paints it; no asset). */
export function compositionBackdropV1(palette: readonly string[], backgroundIndex: number): { gradient: [number, number] } {
  const luminance = (hex: string) => {
    const n = Number.parseInt(hex.slice(1), 16);
    return 0.2126 * ((n >> 16) & 0xff) + 0.7152 * ((n >> 8) & 0xff) + 0.0722 * (n & 0xff);
  };
  if (!Number.isInteger(backgroundIndex) || backgroundIndex < 0 || backgroundIndex >= palette.length || palette.length < 2) {
    throw new Error('Composition backdrop needs a background entry and a second palette entry');
  }
  let bottom = -1;
  palette.forEach((color, index) => {
    if (index !== backgroundIndex && (bottom < 0 || luminance(color) < luminance(palette[bottom]!))) bottom = index;
  });
  return { gradient: [backgroundIndex, bottom] };
}
/** The prompt of a gallery-cover STAND-IN: a byte copy of the accepted art
 * slot `source`, published when the cover could not be generated (no
 * provider call is spent on it). */
export function compositionCoverStandInPromptV1(source: string): string {
  return `cover stand-in: the accepted ${source} art`;
}

/** True when `cover` is the stand-in copy of one of `accepted`: its prompt
 * names that slot and its bytes are that slot's. */
export function isCompositionCoverStandInV1(cover: { prompt?: string; contentSha256?: string },
  accepted: ReadonlyArray<{ name: string; kind: string; contentSha256?: string }>): boolean {
  return !!cover.contentSha256 && accepted.some(row => row.kind !== 'cover' && row.contentSha256 === cover.contentSha256
    && cover.prompt === compositionCoverStandInPromptV1(row.name));
}

/** The asset kind a slot produces. Spec-free by construction: the reserved
 * names are their own kinds, a `<kind>-<state>` name is a strip (presentation
 * kinds can never end in a state), and anything else is an entity sprite. */
export function compositionMediaSlotKindV1(slot: string): CompositionMediaSlotKindV1 {
  if (slot === COMPOSITION_BACKGROUND_SLOT) return 'background';
  if (slot === COMPOSITION_COVER_SLOT) return 'cover';
  if (slot === COMPOSITION_MUSIC_SLOT) return 'music';
  return ANIM_STATE_NAMES.some(state => slot.endsWith(`-${state}`)) ? 'strip' : 'sprite';
}
